import { useApi } from "common/utils/api";
import { useSnackbarAlerts } from "./Snackbar";
import {
  broadCastChannel,
  useStoreSyncedWithLocalStorage
} from "common/store/store";
import { useModals } from "common/utils/modals";
import { graphQLErrorMatchesCode } from "common/utils/errors";
import Grid from "@mui/material/Grid";
import { InfoItem } from "../home/InfoField";
import { frenchFormatDateStringOrTimeStamp } from "common/utils/time";
import { LoadingButton } from "common/components/LoadingButton";
import React, { useMemo } from "react";
import { makeStyles } from "@mui/styles";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoIcon from "@mui/icons-material/Info";
import useTheme from "@mui/styles/useTheme";
import {
  EMPLOYMENT_ROLE,
  EMPLOYMENT_STATUS,
  getEmploymentsStatus
} from "common/utils/employments";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ThirdPartyEmploymentAccess from "./ThirdPartyEmploymentAccess";
import { HideEmail } from "../home/HideEmail";
import { getNextHeadingComponent } from "common/utils/html";
import { formatActivity } from "common/utils/businessTypes";
import Notice from "./Notice";
import { Button } from "@codegouvfr/react-dsfr/Button";
import { Box, Stack } from "@mui/material";
import { useIsWidthDown } from "common/utils/useWidth";
import { FieldTitle } from "./typography/FieldTitle";
import { ExternalLink } from "./ExternalLink";
import { useCompanyCertification } from "./hooks/useCompanyCertification";
import { fr } from "@codegouvfr/react-dsfr";
import { startOfMonth, subMonths } from "date-fns";
import {
  REJECT_EMPLOYMENT_MUTATION,
  VALIDATE_EMPLOYMENT_MUTATION,
  REQUEST_DETACHMENT_MUTATION
} from "common/utils/apiQueries/employments";

const useStyles = makeStyles((theme) => ({
  companyName: {
    fontWeight: 500,
    color: fr.colors.decisions.text.actionHigh.blueFrance.default,
    overflowWrap: "anywhere",
    flexGrow: 1,
    textTransform: "uppercase"
  },
  buttonContainer: {
    padding: theme.spacing(2)
  },
  ended: {
    opacity: 0.5
  },
  employmentDetails: {
    display: "block"
  },
  employmentCard: {
    scrollMarginTop: "100px"
  }
}));

function EMPLOYMENT_STATUS_TO_TEXT_AND_COLOR(theme) {
  return {
    [EMPLOYMENT_STATUS.active]: ["Actif", theme.palette.success.main],
    [EMPLOYMENT_STATUS.pending]: ["À valider", theme.palette.warning.main],
    [EMPLOYMENT_STATUS.future]: ["À venir", theme.palette.warning.main],
    [EMPLOYMENT_STATUS.ended]: ["Terminé", theme.palette.error.main],
    [EMPLOYMENT_STATUS.ceased]: ["Cessée", theme.palette.error.main]
  };
}

const DETACHMENT_COOLDOWN_MS = 48 * 3600 * 1000;
const FULL_WIDTH_BTN_SX = {
  "& .fr-btn": { width: "100%", justifyContent: "center" }
};

export function EmploymentInfoCard({
  employment,
  spacing = 4,
  hideRole = false,
  hideStatus = false,
  hideActions = false,
  showAdminEmails = false,
  lightenIfEnded = true,
  defaultOpen = false,
  hideBusiness = false,
  headingComponent
}) {
  const [open, setOpen] = React.useState(defaultOpen);

  const emailsCurrentAdmins = useMemo(
    () =>
      employment?.company.currentAdmins?.map((admin) => admin.email).join(";"),
    [employment]
  );

  const { isCertified, frenchMedalLabel, TextBadge, CertificationImage } =
    useCompanyCertification(employment.company?.currentCompanyCertification);

  const emailsCurrentAdminsDisplay = useMemo(
    () => (
      <ul
        style={{
          listStyleType: "none",
          paddingInlineStart: "0px",
          margin: "0px"
        }}
      >
        {employment?.company.currentAdmins?.map((admin) => (
          <li
            key={admin.email}
            style={{
              textOverflow: "ellipsis",
              overflow: "hidden",
              maxWidth: "80vw"
            }}
          >
            {admin.email}
          </li>
        ))}
      </ul>
    ),
    [employment]
  );

  const classes = useStyles();

  const api = useApi();
  const alerts = useSnackbarAlerts();

  const store = useStoreSyncedWithLocalStorage();
  const modals = useModals();

  const isMobile = useIsWidthDown("sm");

  const detachmentRequest = employment.detachmentRequest;
  const hasDetachmentRequest = !!detachmentRequest?.requestedAt;
  const lastSentAt = detachmentRequest?.lastSentAt
    ? detachmentRequest.lastSentAt * 1000
    : null;
  const isCooldownActive = lastSentAt
    ? Date.now() - lastSentAt < DETACHMENT_COOLDOWN_MS
    : false;

  React.useEffect(() => {
    if (!hasDetachmentRequest) return;
    const el = document.getElementById(`employment-${employment.id}`);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [hasDetachmentRequest]);

  async function handleDetachmentRequest() {
    await alerts.withApiErrorHandling(async () => {
      const apiResponse = await api.graphQlMutate(
        REQUEST_DETACHMENT_MUTATION,
        { employmentId: employment.id }
      );
      await store.updateEntityObject({
        objectId: employment.id,
        entity: "employments",
        update: apiResponse.data.employments.requestDetachment
      });
      store.batchUpdate();
    });
  }

  async function handleEmploymentValidation(accept) {
    await alerts.withApiErrorHandling(
      async () => {
        const apiResponse = await api.graphQlMutate(
          accept ? VALIDATE_EMPLOYMENT_MUTATION : REJECT_EMPLOYMENT_MUTATION,
          {
            employmentId: employment.id
          }
        );
        if (accept) {
          await store.updateEntityObject({
            objectId: employment.id,
            entity: "employments",
            update: apiResponse.data.employments.validateEmployment,
            createOrReplace: true
          });
        } else await store.deleteEntityObject(employment.id, "employments");

        store.batchUpdate();
        await broadCastChannel.postMessage("update");
      },
      "validate-employment",
      (graphQLError) => {
        if (graphQLErrorMatchesCode(graphQLError, "INVALID_RESOURCE")) {
          return "Opération impossible. Veuillez réessayer ultérieurement.";
        }
      }
    );
  }

  const status = getEmploymentsStatus(employment);

  const [statusText, statusColor] =
    EMPLOYMENT_STATUS_TO_TEXT_AND_COLOR(useTheme())[status];

  return (
    <Accordion
      id={`employment-${employment.id}`}
      variant="outlined"
      expanded={open}
      onChange={(event, open_) => setOpen(open_)}
      className={`${classes.employmentCard} ${
        (status === EMPLOYMENT_STATUS.ended ||
          status === EMPLOYMENT_STATUS.ceased) &&
        lightenIfEnded
          ? classes.ended
          : ""
      }`
      }
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Grid
          container
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
          wrap="nowrap"
        >
          <Grid item xs={8} sm={9}>
            {isMobile ? (
              <Stack direction="column" rowGap={1}>
                {isCertified && <TextBadge />}
                <Typography
                  className={classes.companyName}
                  component={headingComponent}
                >
                  {employment.company.legalName || employment.company.name}
                </Typography>
              </Stack>
            ) : (
              <Stack direction="row">
                <Typography
                  className={classes.companyName}
                  component={headingComponent}
                >
                  {employment.company.legalName || employment.company.name}
                </Typography>
                {isCertified && <TextBadge />}
              </Stack>
            )}
          </Grid>
          {!hideStatus && (
            <Grid item xs={4} sm={3} pr={1}>
              <Typography
                style={{
                  color: statusColor,
                  fontWeight: "bold",
                  textAlign: "right",
                  fontSize: "0.875rem"
                }}
              >
                {statusText}
              </Typography>
            </Grid>
          )}
        </Grid>
      </AccordionSummary>
      <AccordionDetails className={classes.employmentDetails}>
        {!hideActions && hasDetachmentRequest && status === EMPLOYMENT_STATUS.active && !employment.endDate && (
          <Stack spacing="12px" mb="16px" sx={FULL_WIDTH_BTN_SX}>
            <Notice
              type="info"
              size="small"
              description={
                detachmentRequest.lastSentAt !== detachmentRequest.requestedAt
                  ? `Relance de demande de détachement envoyée le ${new Date(detachmentRequest.lastSentAt * 1000).toLocaleDateString("fr-FR")}.`
                  : `Demande de détachement envoyée le ${new Date(detachmentRequest.requestedAt * 1000).toLocaleDateString("fr-FR")}.`
              }
            />
            <Button
              priority="secondary"
              size="small"
              disabled={isCooldownActive}
              sx={{ width: "100%", justifyContent: "center" }}
              onClick={() => handleDetachmentRequest()}
            >
              Relancer le gestionnaire
            </Button>
            {isCooldownActive && (
              <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <InfoIcon
                  aria-hidden="true"
                  sx={{ color: fr.colors.decisions.text.default.info.default, fontSize: 16 }}
                />
                <Typography sx={{
                  fontSize: 14,
                  color: fr.colors.decisions.text.default.info.default
                }}>
                  Relance possible au bout de 48h.
                </Typography>
              </Box>
            )}
          </Stack>
        )}
        <Grid container wrap="wrap" spacing={spacing}>
          <Grid item xs={6}>
            <InfoItem
              name="SIREN"
              value={employment.company.siren}
              titleProps={{
                component: getNextHeadingComponent(headingComponent)
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <InfoItem
              name="SIRETS"
              value={employment.company.sirets?.join(", ")}
              titleProps={{
                component: getNextHeadingComponent(headingComponent)
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <InfoItem
              name="Début rattachement"
              value={frenchFormatDateStringOrTimeStamp(employment.startDate)}
              titleProps={{
                component: getNextHeadingComponent(headingComponent)
              }}
              uppercaseTitle={false}
            />
          </Grid>
          <Grid item xs={6}>
            <InfoItem
              name="Fin rattachement"
              value={
                employment.endDate
                  ? frenchFormatDateStringOrTimeStamp(employment.endDate)
                  : ""
              }
              titleProps={{
                component: getNextHeadingComponent(headingComponent)
              }}
              uppercaseTitle={false}
            />
          </Grid>
          {!hideBusiness && !!employment.business && (
            <Grid item xs={12}>
              <InfoItem
                name="Type d'activité"
                value={formatActivity(employment.business)}
                titleProps={{
                  component: getNextHeadingComponent(headingComponent)
                }}
                uppercaseTitle={false}
              />
            </Grid>
          )}
          {!!showAdminEmails && !!emailsCurrentAdminsDisplay && (
            <Grid item>
              <InfoItem
                name="Email(s) gestionnaire(s)"
                value={emailsCurrentAdminsDisplay}
                titleProps={{
                  component: getNextHeadingComponent(headingComponent)
                }}
                uppercaseTitle={false}
              />
              <Button
                priority="tertiary"
                onClick={() => {
                  navigator.clipboard
                    .writeText(emailsCurrentAdmins)
                    .then(() => {
                      alerts.success(
                        `Email(s) copiée(s) dans le presse-papiers !`,
                        "",
                        2000
                      );
                    });
                }}
              >
                copier les emails
              </Button>
            </Grid>
          )}
          {!hideRole && (
            <Grid item xs={12}>
              <InfoItem
                name="Rôle"
                value={
                  employment.hasAdminRights
                    ? EMPLOYMENT_ROLE.admin
                    : EMPLOYMENT_ROLE.employee
                }
                titleProps={{
                  component: getNextHeadingComponent(headingComponent)
                }}
                uppercaseTitle={false}
              />
            </Grid>
          )}
          {isCertified && (
            <Grid item xs={12}>
              <Stack direction="column" sx={{ flexGrow: 1 }} rowGap={2}>
                <Box>
                  <FieldTitle uppercaseTitle={false}>Certificat</FieldTitle>
                  <Typography align="left">
                    L'entreprise est certifiée{" "}
                    <span style={{ fontWeight: "bold" }}>
                      {frenchMedalLabel}
                    </span>{" "}
                    !
                  </Typography>
                </Box>
                <CertificationImage />
                <ExternalLink
                  url="https://faq.mobilic.beta.gouv.fr/usages-et-fonctionnement-de-mobilic-gestionnaire/comment-obtenir-le-certificat-mobilic/"
                  text="Qu'est-ce que le certificat Mobilic ?"
                  withIcon
                />
              </Stack>
            </Grid>
          )}
          {!hideActions && !!employment.authorizedClients?.length && (
            <Grid item xs={12}>
              <ThirdPartyEmploymentAccess
                employmentId={employment.id}
                clients={employment.authorizedClients}
              />
            </Grid>
          )}
          {!hideActions && (
            <Grid item xs={12} sx={{ "& .fr-fieldset": { marginBottom: "12px" } }}>
              <HideEmail
                employment={employment}
                disabled={
                  status === EMPLOYMENT_STATUS.ended ||
                  status === EMPLOYMENT_STATUS.ceased
                }
              />
            </Grid>
          )}
        </Grid>

        {!hideStatus &&
          !hideActions &&
          (status === EMPLOYMENT_STATUS.ended ||
            status === EMPLOYMENT_STATUS.ceased) && (
            <Notice
              type="warning"
              description="L'entreprise a mis un terme à votre rattachement. Vous ne pouvez
            plus saisir de temps de travail pour cette entreprise."
            />
          )}
        {!hideActions &&
          !employment.hasAdminRights &&
          status === EMPLOYMENT_STATUS.active &&
          !employment.endDate && (
            <Stack spacing="12px" sx={FULL_WIDTH_BTN_SX}>
              <Button
                priority="secondary"
                size="small"
                onClick={() =>
                  modals.open("pdfExport", {
                    initialMinDate: startOfMonth(subMonths(new Date(), 1)),
                    initialMaxDate: new Date()
                  })
                }
              >
                Télécharger un récapitulatif d'heures
              </Button>
              {!hasDetachmentRequest && (
                <Button
                  priority="secondary"
                  size="small"
                  iconId="fr-icon-logout-box-r-line"
                  iconPosition="right"
                  onClick={() =>
                    modals.open("confirmation", {
                      textButtons: true,
                      title: `Quitter l'entreprise ${employment.company.name}`,
                      content:
                        "Confirmez-vous l'envoi d'une demande de détachement de votre compte Mobilic à l'entreprise ?",
                      handleConfirm: handleDetachmentRequest
                    })
                  }
                >
                  Quitter l'entreprise
                </Button>
              )}
            </Stack>
          )}
        {!hideStatus &&
          !hideActions &&
          status === EMPLOYMENT_STATUS.pending && (
            <>
              <Notice
                description="La validation du rattachement vous donnera le droit d'enregistrer
              du temps de travail pour cette entreprise."
              />
              <Grid
                className={classes.buttonContainer}
                container
                justifyContent="space-evenly"
                spacing={2}
              >
                <Grid item>
                  <LoadingButton
                    onClick={async () => await handleEmploymentValidation(true)}
                  >
                    Valider le rattachement
                  </LoadingButton>
                </Grid>
                <Grid item>
                  <LoadingButton
                    priority="secondary"
                    onClick={() =>
                      modals.open("confirmation", {
                        textButtons: true,
                        title: "Confirmer rejet du rattachement",
                        handleConfirm: async () =>
                          await handleEmploymentValidation(false)
                      })
                    }
                  >
                    Rejeter
                  </LoadingButton>
                </Grid>
              </Grid>
            </>
          )}
      </AccordionDetails>
    </Accordion>
  );
}
